/**
 * GlobalImageDefaults
 *
 * Sitewide Core Web Vitals safety net for raw <img> tags that authors forgot
 * to migrate to <OptimizedImage>. Applies sensible defaults via DOM scan +
 * MutationObserver so the change propagates to every route automatically:
 *
 *   - loading="lazy"      when the tag has no explicit loading attribute
 *                         and is NOT marked as the LCP candidate
 *   - decoding="async"    when no decoding hint is set
 *   - fetchpriority="low" for the same set, to deprioritize off-screen images
 *
 * An author can always opt out by setting `loading="eager"` /
 * `fetchpriority="high"` / `data-lcp` / `data-priority` explicitly — those
 * tags are never touched.
 *
 * The very first <img> already in the DOM at hydration is left alone so we
 * don't downgrade what is most likely the LCP image. Subsequent images
 * (lazy-rendered sections, route transitions) always get the defaults.
 *
 * Perf: observer only inspects newly-added nodes (not full document re-walks),
 * is debounced (~200ms) via requestIdleCallback, and processed nodes are
 * flagged in a WeakSet so each <img> is touched at most once.
 */
import { useEffect } from 'react';

const SKIP_ATTRS = ['data-lcp', 'data-priority', 'data-no-perf'];
const processed = new WeakSet<HTMLImageElement>();

function shouldSkip(img: HTMLImageElement): boolean {
  if (SKIP_ATTRS.some(attr => img.hasAttribute(attr))) return true;
  if (img.getAttribute('fetchpriority') === 'high') return true;
  if (img.getAttribute('loading') === 'eager') return true;
  return false;
}

function applyDefaults(img: HTMLImageElement) {
  if (processed.has(img)) return;
  processed.add(img);
  if (shouldSkip(img)) return;
  if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
  if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
  if (!img.hasAttribute('fetchpriority')) img.setAttribute('fetchpriority', 'low');
}

type IdleScheduler = (cb: () => void) => number;
const scheduleIdle: IdleScheduler =
  typeof window !== 'undefined' && (window as any).requestIdleCallback
    ? (cb) => (window as any).requestIdleCallback(cb, { timeout: 500 })
    : (cb) => window.setTimeout(cb, 200);

export function GlobalImageDefaults() {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Skip the first <img> in the initial DOM — that's almost always the
    // LCP candidate and the page author is responsible for its priority.
    const initial = Array.from(document.images);
    if (initial[0]) processed.add(initial[0]);
    initial.slice(1).forEach(applyDefaults);

    // Buffer newly-added image nodes only; never re-walk the whole document.
    const pending: HTMLImageElement[] = [];
    let scheduled = false;
    let debounceTimer = 0;

    const flush = () => {
      scheduled = false;
      const batch = pending.splice(0, pending.length);
      for (const img of batch) applyDefaults(img);
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => scheduleIdle(flush), 200);
    };

    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        m.addedNodes.forEach(node => {
          if (node instanceof HTMLImageElement) {
            if (!processed.has(node)) pending.push(node);
          } else if (node instanceof HTMLElement) {
            const imgs = node.querySelectorAll('img');
            for (let i = 0; i < imgs.length; i++) {
              const im = imgs[i] as HTMLImageElement;
              if (!processed.has(im)) pending.push(im);
            }
          }
        });
      }
      if (pending.length) schedule();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      window.clearTimeout(debounceTimer);
    };
  }, []);

  return null;
}
